/*
    Copyright 2022 Dynatrace LLC

    Licensed under the Apache License, Version 2.0 (the "License");
    you may not use this file except in compliance with the License.
    You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

    Unless required by applicable law or agreed to in writing, software
    distributed under the License is distributed on an "AS IS" BASIS,
    WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
    See the License for the specific language governing permissions and
    limitations under the License.
*/

import { performance } from "perf_hooks";

import { HrTime } from "@opentelemetry/api";

// ----------------------------------------------------------------------------
export enum SendState {
	New,
	Skip,
	Drop,
	Update,
	Alive,
	Finished
}

// ----------------------------------------------------------------------------
export enum PrepareResult {
	Drop,
	Send,
	Skip
}

// ----------------------------------------------------------------------------
export interface TransmitOptions {
	readonly updateIntervalMs: number;
	readonly keepAliveIntervalMs: number;
	readonly openSpanTimeoutMs: number;
}

// ----------------------------------------------------------------------------
/**
 * Meta data assoziated to a Span needed by DtSpanProcessor/DtSpanExporter/Propagator.
 */
export class SpanMetaData {
	constructor(private readonly _options: TransmitOptions) {
	}

	public prepareSend(now: number, isFinished: boolean): PrepareResult {
		const shouldSend = this._prepareSend(now, isFinished);
		if (shouldSend === PrepareResult.Send) {
			this._lastSentMs = now;
			this._seqNr++;
		}
		return shouldSend;
	}

	public get seqNr(): number {
		return this._seqNr;
	}

	public get sendState(): SendState {
		return this._sendState;
	}

	public lastPropagationTime?: HrTime;
	public tenantParentSpanId?: string;
	public mobileTag?: string;
	public encodedLinkId?: number;

	private _prepareSend(now: number, isFinished: boolean): PrepareResult {
		// always send finished spans
		if (isFinished) {
			this._sendState = SendState.Finished;
			return PrepareResult.Send;
		}

		// drop outdated, non finished spans
		const ageMs = now - this._firstSeenMs;
		if (ageMs >= this._options.openSpanTimeoutMs) {
			this._sendState = SendState.Drop;
			return PrepareResult.Drop;
		}

		// new, non finished spans should be sent only if they are old enough
		if (this._sendState === SendState.New) {
			if (ageMs >= this._options.updateIntervalMs) {
				this._sendState = SendState.Update;
				return PrepareResult.Send;
			}
			return PrepareResult.Skip;
		}

		// Open spans should be sent as keep alive
		if (now - this._lastSentMs >= this._options.keepAliveIntervalMs) {
			this._sendState = SendState.Alive;
			return PrepareResult.Send;
		}

		this._sendState = SendState.Skip;
		return PrepareResult.Skip;
	}

	private _firstSeenMs = performance.now();
	private _sendState = SendState.New;
	private _lastSentMs = 0;
	private _seqNr = -1;
}
